import React, { useState, useEffect } from 'react';
import { getAgencyList, createFundingApplication } from '../../services/funding.service';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay.component';
import { Button, makeStyles, TextField, Typography, Grid, Select, MenuItem, InputLabel } from '@material-ui/core';
import Dialog from '../ui/Dialog';

const AddApplication = ({ data }) => {
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmation, setConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [errorInputMessage, setErrorInputMessage] = useState(null);
  const [touchedFields, setTouchedFields] = useState({
    agency_name: false,
    coordinator_notes: false
  });
  
  const [applicationDetails, setApplicationDetails] = useState({
    agency_name: '',
    coordinator_notes: ''
  });
  
  const [agencyList, setAgencyList] = useState([]);
  const { match } = data;
  const programId = match.params.connect_program_id;
  const queryString = window.location.search;
  const searchParams = new URLSearchParams(queryString);
  const patient_id = searchParams.get('patient_id');

  const useStyles = makeStyles(() => ({
    field: {
      marginBottom: 10,
      width: '100%',
      minWidth: '500px'
    },
    select: {
      width: '100%'
    },
    formControl: {
      width: '100%'
    },
    customWidth: {
      '& .MuiDialog-paper': {
        width: '800px',
        maxWidth: '800px',
      }
    },
    errorText: {
      color: 'red',
      fontSize: '0.75rem',
      marginTop: 3,
    }
  }));

  const classes = useStyles();

  useEffect(() => {
    const fetchAgencyList = async () => {
      try {
        const { result } = await getAgencyList({
          program_id: programId                
        });
        
        let agencyList = [];
        
        if (result) {
          agencyList = result;
        }
        
        agencyList.unshift({ label: 'Select Agency', value: '' });
        setAgencyList(agencyList);
      } catch (error) {
        console.error('Error fetching agency list:', error);
      }
    };

    fetchAgencyList();
  }, []);

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!applicationDetails.agency_name) {
      errors.agency_name = 'Agency Name is required';
      isValid = false;
    }

    if (!applicationDetails.coordinator_notes) {
      errors.coordinator_notes = "Coordinator's Notes are required";
      isValid = false;
    }

    setErrorInputMessage(errors);
    return isValid;
  };

  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
    
    if (!applicationDetails[fieldName]) {
      setErrorInputMessage(prev => ({
        ...prev,
        [fieldName]: `${fieldName.replace('_', ' ')} is required`
      }));
    }
  };

  const onClick = () => {
    setOpenModal(true);
  };
 
  const onSubmit = async() => {
    if (!validateForm()) {
      setTouchedFields({
        agency_name: true,
        coordinator_notes: true
      });
      return;
    }

    try {
      setLoading(true);
      const { result } = await createFundingApplication({
        program_id: programId,
        payload: {
          funding_applications: [{
            user_id: patient_id, 
            funding_agency_id: applicationDetails.agency_name,        
            notes: applicationDetails.coordinator_notes
          }]
        }
      });

      if (result) {
        setOpenModal(false);
        setConfirmation(true);
      }
    } catch(err) {
      if (err && err.inputs) {
        setErrorInputMessage(err.inputs);
      } else if (err && err.message) {
        setErrorMessage(err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  const onFieldInput = (e) => {
    const { name, value } = e.target;
    setApplicationDetails(prevState => ({
      ...prevState,
      [name]: value
    }));

   if (value && errorInputMessage?.[name]) {
      setErrorInputMessage(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleClose = (reload) => {
    setOpenModal(false);
    setConfirmation(false);
    setErrorMessage(null);
    setErrorInputMessage(null);
    setTouchedFields({
      agency_name: false,
      coordinator_notes: false
    });
    setApplicationDetails({
      agency_name: '',
      coordinator_notes: ''
    });

    if (reload) {
      window.location.reload();
    }
  };

  const formContent = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InputLabel>Agency Name *</InputLabel>
          <Select
            required
            label='Agency Name'
            name="agency_name"
            value={applicationDetails.agency_name}
            variant='outlined'
            className={classes.field}
            onChange={onFieldInput}
            onBlur={() => handleFieldBlur('agency_name')}
            error={!!(touchedFields.agency_name && errorInputMessage?.agency_name)}
          >
            {agencyList.map(agency => (
              <MenuItem key={agency.id} value={agency.id}>
                {agency.funding_agency_name}
              </MenuItem>
            ))}
          </Select>
          {touchedFields.agency_name && errorInputMessage?.agency_name && (
            <Typography className={classes.errorText}>
              {errorInputMessage.agency_name}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            label="Program Coordinator's Notes *"
            name="coordinator_notes"
            value={applicationDetails.coordinator_notes}
            variant='outlined'
            className={classes.field}
            onChange={onFieldInput}
            onBlur={() => handleFieldBlur('coordinator_notes')}
            error={!!(touchedFields.coordinator_notes && errorInputMessage?.coordinator_notes)}
            multiline
            rows={4}
            fullWidth
          />
          {touchedFields.coordinator_notes && errorInputMessage?.coordinator_notes && (
            <Typography className={classes.errorText}>
              {errorInputMessage.coordinator_notes}
            </Typography>
          )}
        </Grid>
      </Grid>
      {errorMessage && (
        <Typography className={classes.errorText}>
          {errorMessage}
        </Typography>
      )}
    </>
  );

  const formButtons = (
    <>
      <Button onClick={() => handleClose(false)} variant='contained'>
        Close
      </Button>
      <Button 
        onClick={onSubmit} 
        variant='contained' 
        color='secondary'
        disabled={isLoading}
      >
        {isLoading ? 'Submitting...' : 'Submit'}
      </Button>
    </>
  );

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <>
      <Dialog
        className={classes.customWidth}
        open={openModal}
        title='Add New Application'
        content={formContent}
        children={formButtons}
        onSubmit={onSubmit}
        onClose={() => handleClose(false)}
      />
      <Dialog 
        key='confirmation'
        open={openConfirmation}
        title='Confirmation'
        content='Application successfully created!' 
        children={(
          <Button onClick={() => handleClose(true)} variant='contained'>
            Close
          </Button>
        )} 
        onSubmit={onSubmit}
        onClose={() => handleClose(true)} 
      />
      <Button
        variant="contained"
        color="primary"
        onClick={onClick}
        style={{ marginRight: 8, marginBottom: 10 }}
      >
        + Add Application
      </Button>
    </>
  );
};

export default AddApplication;